import { Chip, styled, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Exclude from "../common/Exclude";
import Favourite from "../common/Favourite";
import theme from "../styles/theme";
import { DbSession, ExerciseId } from "../types";
import { getSummaryFromSession } from "../utils/exercises";
import { secondsToTime } from "../utils/timeHelpers";
import { useFavouritesAndExcluded } from "../utils/useFavouritesAndExcluded";

interface Props {
    session: DbSession;
}

const ExerciseOverview: React.FunctionComponent<Props> = ({ session }) => {
    const { t, i18n } = useTranslation();

    const sessionByCategory = getSummaryFromSession(session);

    const { favourites, setFavourite, excluded, setExcluded } = useFavouritesAndExcluded();

    const handleFavouriteClick = (id: ExerciseId, value: number | null) => {
        setFavourite(id, !!value);
    };

    const handleExcludeClick = (id: ExerciseId, value: number | null) => {
        setExcluded(id, !!value);
    };

    const categoriesForDisplay = useMemo(
        () =>
            Object.entries(sessionByCategory)
                .filter(([, { duration }]) => duration > 0)
                .map(([category, { exercises, duration }]) => ({
                    category: category as keyof typeof sessionByCategory,
                    exercises,
                    duration,
                })),
        [sessionByCategory],
    );

    return (
        <>
            <Typography color="primary" fontWeight="700" style={{ marginTop: 20, marginBottom: 10, fontSize: 20 }}>
                {t("exercises")}
            </Typography>
            {categoriesForDisplay.map(({ category, duration, exercises }) => (
                <div key={category}>
                    <CategoryContainer>
                        <Chip
                            style={{
                                backgroundColor: theme.colors[category],
                                color: "white",
                            }}
                            label={t(category)}
                        />
                        <Typography style={{ marginLeft: 10, fontSize: 18 }}>{`${secondsToTime(duration)} min`}</Typography>
                    </CategoryContainer>
                    {
                        <ul>
                            {exercises.map((exercise) => (
                                <li key={exercise.nameEn}>
                                    <ListItem>
                                        <Typography>{i18n.language === "de" ? exercise.nameDe : exercise.nameEn} </Typography>
                                        <Favourite favourites={favourites} excluded={excluded} id={exercise.id} handleClick={handleFavouriteClick} />
                                        <Exclude excluded={excluded} favourites={favourites} id={exercise.id} handleClick={handleExcludeClick} />
                                    </ListItem>
                                </li>
                            ))}
                        </ul>
                    }
                </div>
            ))}
        </>
    );
};

export default ExerciseOverview;

const CategoryContainer = styled("div")`
    display: flex;
    align-items: center;
`;

const ListItem = styled("div")`
    display: flex;
`;
